// src/features/simulators/shared/utils/AnalyticsClient.ts

import type {
  RegisterDeviceRequest,
  RegisterDeviceResponse,
  SendEventRequest,
  SendCheckInRequest,
  UpdateJourneyRequest,
  CapturedRequest,
} from '../types/simulator.types';

export type RequestCaptureCallback = (request: CapturedRequest) => void;

/**
 * Client for Analytics API
 * Handles device registration and event sending for all simulators
 */
export class AnalyticsClient {
  private apiUrl: string;
  private apiKey: string | null = null;
  private onCapture: RequestCaptureCallback | null = null;

  constructor(apiUrl: string) {
    this.apiUrl = apiUrl;
  }

  /**
   * Enable request capture for API log / Postman export
   */
  setRequestCapture(callback: RequestCaptureCallback | null): void {
    this.onCapture = callback;
  }

  /**
   * Internal: execute a fetch and optionally capture the request/response
   */
  private async trackedFetch(
    url: string,
    init: RequestInit,
    label: string,
  ): Promise<Response> {
    const startTime = Date.now();
    const response = await fetch(url, init);

    if (this.onCapture) {
      const cloned = response.clone();
      let responseBody: any;
      try {
        responseBody = await cloned.json();
      } catch {
        responseBody = null;
      }

      const headers: Record<string, string> = {};
      if (init.headers instanceof Headers) {
        init.headers.forEach((v, k) => { headers[k] = v; });
      } else if (init.headers) {
        Object.entries(init.headers).forEach(([k, v]) => { headers[k] = v as string; });
      }

      const captured: CapturedRequest = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        timestamp: new Date(startTime),
        method: (init.method || 'GET').toUpperCase(),
        url,
        headers,
        body: init.body ? JSON.parse(init.body as string) : undefined,
        responseStatus: response.status,
        responseBody,
        duration: Date.now() - startTime,
        label,
      };

      this.onCapture(captured);
    }

    return response;
  }

  /**
   * Register device with Analytics API
   */
  async registerDevice(request: RegisterDeviceRequest): Promise<RegisterDeviceResponse> {
    const payload = {
      installationCode: request.installationCode,
    };

    const response = await this.trackedFetch(
      `${this.apiUrl}/register`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
      'Register Device',
    );

    if (!response.ok) {
      let errorMessage = 'Device registration failed';
      try {
        const error = await response.json();
        errorMessage = error.error?.message || error.message || errorMessage;
      } catch {
        errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();

    if (result.success && result.data.apiKey) {
      this.apiKey = result.data.apiKey;
    }

    return result;
  }

  /**
   * Send event to Analytics API
   */
  async sendEvent(event: SendEventRequest): Promise<any> {
    if (!this.apiKey) {
      throw new Error('Device not registered. Call registerDevice() first.');
    }

    const eventCode = event.eventCode || 'UNKNOWN';
    const label = eventCode === 'HB'
      ? 'Heartbeat'
      : `Send Event (${eventCode})`;

    const response = await this.trackedFetch(
      `${this.apiUrl}/events`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Api-Key': this.apiKey,
          'X-Device-Id': event.deviceId,
        },
        body: JSON.stringify(event),
      },
      label,
    );

    if (!response.ok) {
      let errorMessage = 'Event send failed';
      try {
        const error = await response.json();
        errorMessage = error.error?.message || error.message || errorMessage;
      } catch {
        errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  }

  /**
   * Send check-in record to Analytics API
   */
  async sendCheckIn(checkIn: SendCheckInRequest): Promise<any> {
    if (!this.apiKey) {
      throw new Error('Device not registered. Call registerDevice() first.');
    }

    const response = await this.trackedFetch(
      `${this.apiUrl}/check-ins`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Api-Key': this.apiKey,
        },
        body: JSON.stringify(checkIn),
      },
      'Send Check-in',
    );

    if (!response.ok) {
      let errorMessage = 'Check-in send failed';
      try {
        const error = await response.json();
        errorMessage = error.error?.message || error.message || errorMessage;
      } catch {
        errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  }

  /**
   * Update journey with event
   */
  async updateJourney(update: UpdateJourneyRequest): Promise<any> {
    if (!this.apiKey) {
      throw new Error('Device not registered. Call registerDevice() first.');
    }

    const response = await this.trackedFetch(
      `${this.apiUrl}/journeys/update`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Api-Key': this.apiKey,
        },
        body: JSON.stringify(update),
      },
      `Update Journey (${update.eventType})`,
    );

    if (!response.ok) {
      let errorMessage = 'Journey update failed';
      try {
        const error = await response.json();
        errorMessage = error.error?.message || error.message || errorMessage;
      } catch {
        errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  }

  getApiKey(): string | null {
    return this.apiKey;
  }

  setApiKey(apiKey: string): void {
    this.apiKey = apiKey;
  }

  isRegistered(): boolean {
    return this.apiKey !== null;
  }

  clearRegistration(): void {
    this.apiKey = null;
  }
}
